import React, { useState, useEffect } from 'react';
import { 
  CognitoUserPool, 
  CognitoUser, 
  AuthenticationDetails 
} from 'amazon-cognito-identity-js';
import { 
  PersonIcon, 
  MailIcon, 
  LockIcon, 
  EyeIcon, 
  EyeClosedIcon, 
  XIcon, 
  KeyIcon,
  CheckIcon,
  SignOutIcon
} from '@primer/octicons-react';
import './AuthModal.css';

const AuthModal = ({ isOpen, onClose, onLoginSuccess, user }) => {
  const poolData = {
    UserPoolId: process.env.REACT_APP_AWS_COGNITO_USER_POOL_ID,
    ClientId: process.env.REACT_APP_AWS_COGNITO_CLIENT_ID
  };
  
  const userPool = new CognitoUserPool(poolData);

  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [resetData, setResetData] = useState({
    email: '',
    otp: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [countdown, setCountdown] = useState(0);
  const [cognitoUser, setCognitoUser] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
    });
    setResetData({
      email: '',
      otp: '',
      newPassword: '',
      confirmNewPassword: '',
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
    setShowNewPassword(false);
    setShowConfirmNewPassword(false);
    setMessage({ text: '', type: '' });
    setIsForgotPassword(false);
    setResetStep(1);
    setCountdown(0);
    setCognitoUser(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleResetChange = (e) => {
    setResetData({
      ...resetData,
      [e.target.name]: e.target.value,
    });
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    return {
      isValid: password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar,
      errors: [
        { condition: password.length >= minLength, message: 'At least 8 characters' },
        { condition: hasUpperCase, message: 'At least one uppercase letter' },
        { condition: hasLowerCase, message: 'At least one lowercase letter' },
        { condition: hasNumbers, message: 'At least one number' },
        { condition: hasSpecialChar, message: 'At least one special character' },
      ]
    };
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });

    const authenticationData = {
      Username: formData.email,
      Password: formData.password,
    };

    const authenticationDetails = new AuthenticationDetails(authenticationData);

    const userData = {
      Username: formData.email,
      Pool: userPool,
    };

    const cognitoUser = new CognitoUser(userData);

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        const accessToken = result.getAccessToken().getJwtToken();
        const idToken = result.getIdToken().getJwtToken();
        const refreshToken = result.getRefreshToken().getToken();

        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('idToken', idToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('email', formData.email);

        cognitoUser.getUserAttributes((err, attributes) => {
          if (err) {
            setMessage({ text: 'Login successful!', type: 'success' });
            onLoginSuccess({ email: formData.email });
          } else {
            const nameAttr = attributes.find(attr => attr.Name === 'name');
            const userName = nameAttr ? nameAttr.Value : formData.email.split('@')[0];
            
            localStorage.setItem('userName', userName);
            setMessage({ text: 'Login successful!', type: 'success' });
            onLoginSuccess({ 
              email: formData.email, 
              name: userName 
            });
          }
          
          setTimeout(() => {
            onClose();
          }, 1500);
        });
      },
      onFailure: (err) => {
        let errorMessage = 'Login failed';
        
        if (err.code === 'UserNotConfirmedException') {
          errorMessage = 'Please confirm your email address first';
        } else if (err.code === 'NotAuthorizedException') {
          errorMessage = 'Incorrect email or password';
        } else if (err.code === 'UserNotFoundException') {
          errorMessage = 'User not found. Please register first';
        }
        
        setMessage({
          text: errorMessage,
          type: 'error'
        });
        setLoading(false);
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        setMessage({
          text: 'New password required. Please contact administrator.',
          type: 'error'
        });
        setLoading(false);
      }
    });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });

    if (formData.password !== formData.confirmPassword) {
      setMessage({ text: 'Passwords do not match', type: 'error' });
      setLoading(false);
      return;
    }

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      setMessage({ text: 'Password does not meet requirements', type: 'error' });
      setLoading(false);
      return;
    }

    const attributeList = [
      {
        Name: 'email',
        Value: formData.email,
      },
      {
        Name: 'name',
        Value: formData.name,
      },
    ];

    userPool.signUp(
      formData.email,
      formData.password,
      attributeList,
      null,
      (err, result) => {
        if (err) {
          let errorMessage = 'Registration failed';
          
          if (err.code === 'UsernameExistsException') {
            errorMessage = 'User already exists. Please login instead';
          } else if (err.code === 'InvalidPasswordException') {
            errorMessage = 'Password does not meet requirements';
          } else if (err.code === 'InvalidParameterException') {
            errorMessage = 'Invalid email format';
          }
          
          setMessage({
            text: errorMessage,
            type: 'error'
          });
          setLoading(false);
          return;
        }

        const cognitoUser = result.user;
        
        setMessage({ 
          text: 'Registration successful! Please check your email for confirmation code.', 
          type: 'success' 
        });
        
        setResetStep(2);
        setResetData(prev => ({ ...prev, email: formData.email }));
        setIsForgotPassword(false);
        setCognitoUser(new CognitoUser({
          Username: formData.email,
          Pool: userPool
        }));
        setLoading(false);
      }
    );
  };

  const handleConfirmRegistration = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });

    if (!cognitoUser) {
      setMessage({ text: 'User not found. Please try registering again.', type: 'error' });
      setLoading(false);
      return;
    }

    cognitoUser.confirmRegistration(resetData.otp, true, (err, result) => {
      if (err) {
        setMessage({
          text: err.message || 'Failed to confirm registration',
          type: 'error'
        });
        setLoading(false);
        return;
      }

      setMessage({ 
        text: 'Email confirmed successfully! You can now login.', 
        type: 'success' 
      });

      setTimeout(() => {
        resetForm();
        setIsLogin(true);
        setLoading(false);
      }, 2000);
    });
  };

  const handleForgotPassword = async () => {
    if (!resetData.email) {
      setMessage({ text: 'Please enter your email address', type: 'error' });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    const userData = {
      Username: resetData.email,
      Pool: userPool,
    };

    const cognitoUser = new CognitoUser(userData);
    setCognitoUser(cognitoUser);

    cognitoUser.forgotPassword({
      onSuccess: (data) => {
        setMessage({ 
          text: 'OTP sent to your email. Please check your inbox.', 
          type: 'success' 
        });
        setResetStep(2);
        setCountdown(300);
        setLoading(false);
      },
      onFailure: (err) => {
        let errorMessage = 'Failed to send OTP';
        
        if (err.code === 'UserNotFoundException') {
          errorMessage = 'User not found. Please check your email address';
        } else if (err.code === 'InvalidParameterException') {
          errorMessage = 'Invalid email address';
        }
        
        setMessage({
          text: errorMessage,
          type: 'error'
        });
        setLoading(false);
      }
    });
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });

    if (resetData.newPassword !== resetData.confirmNewPassword) {
      setMessage({ text: 'Passwords do not match', type: 'error' });
      setLoading(false);
      return;
    }

    const passwordValidation = validatePassword(resetData.newPassword);
    if (!passwordValidation.isValid) {
      setMessage({ 
        text: 'Password does not meet requirements', 
        type: 'error' 
      });
      setLoading(false);
      return;
    }

    if (!resetData.otp || resetData.otp.length < 6) {
      setMessage({ text: 'Please enter a valid OTP', type: 'error' });
      setLoading(false);
      return;
    }

    if (!cognitoUser) {
      setMessage({ text: 'Session expired. Please restart the password reset process.', type: 'error' });
      setLoading(false);
      return;
    }

    cognitoUser.confirmPassword(resetData.otp, resetData.newPassword, {
      onSuccess: () => {
        setMessage({ 
          text: 'Password reset successfully! You can now login with your new password.', 
          type: 'success' 
        });

        setTimeout(() => {
          resetForm();
          setIsForgotPassword(false);
          setIsLogin(true);
          setLoading(false);
        }, 2000);
      },
      onFailure: (err) => {
        let errorMessage = 'Failed to reset password';
        
        if (err.code === 'CodeMismatchException') {
          errorMessage = 'Invalid OTP. Please check and try again';
        } else if (err.code === 'ExpiredCodeException') {
          errorMessage = 'OTP has expired. Please request a new one';
        } else if (err.code === 'InvalidPasswordException') {
          errorMessage = 'Password does not meet requirements';
        }
        
        setMessage({
          text: errorMessage,
          type: 'error'
        });
        setLoading(false);
      }
    });
  };

  const resendOTP = async () => {
    if (countdown > 0) {
      setMessage({ 
        text: `Please wait ${countdown} seconds before requesting a new OTP`, 
        type: 'error' 
      });
      return;
    }

    setLoading(true);
    try {
      if (cognitoUser) {
        cognitoUser.forgotPassword({
          onSuccess: (data) => {
            setMessage({ 
              text: 'New OTP sent to your email', 
              type: 'success' 
            });
            setCountdown(300);
            setLoading(false);
          },
          onFailure: (err) => {
            setMessage({
              text: 'Failed to resend OTP',
              type: 'error'
            });
            setLoading(false);
          }
        });
      } else {
        const userData = {
          Username: resetData.email,
          Pool: userPool,
        };
        
        const cognitoUser = new CognitoUser(userData);
        cognitoUser.resendConfirmationCode((err, result) => {
          if (err) {
            setMessage({
              text: 'Failed to resend confirmation code',
              type: 'error'
            });
            setLoading(false);
            return;
          }
          
          setMessage({ 
            text: 'New confirmation code sent to your email', 
            type: 'success' 
          });
          setCountdown(300);
          setLoading(false);
        });
      }
    } catch (error) {
      setMessage({
        text: 'Failed to resend OTP',
        type: 'error'
      });
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLogin) {
      await handleLogin(e);
    } else {
      await handleRegister(e);
    }
  };

  const handleBackToLogin = () => {
    resetForm();
    setIsForgotPassword(false);
  };

  const isConfirmingRegistration = cognitoUser && !isForgotPassword && resetStep === 2;

  if (!isOpen) return null;

  const passwordValidation = isForgotPassword 
    ? validatePassword(resetData.newPassword)
    : validatePassword(formData.password);

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={onClose}>
          <XIcon size={24} />
        </button>

        <div className="auth-modal-header">
          <h2>
            {isConfirmingRegistration 
              ? 'Confirm Email' 
              : isForgotPassword 
                ? resetStep === 1 ? 'Forgot Password' : 'Reset Password'
                : isLogin ? 'Login' : 'Register'
            }
          </h2>
          {!isForgotPassword && !isConfirmingRegistration && (
            <p>
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <button 
                className="auth-toggle-btn"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setMessage({ text: '', type: '' });
                }}
              >
                {isLogin ? 'Register' : 'Login'}
              </button>
            </p>
          )}
        </div>

        {message.text && (
          <div className={`auth-message ${message.type}`}>
            {message.type === 'success' && <CheckIcon size={16} />}
            {message.text}
          </div>
        )}

        {isConfirmingRegistration ? (
          <form onSubmit={handleConfirmRegistration}>
            <div className="form-group">
              <label htmlFor="otp">
                <KeyIcon size={16} />
                Confirmation Code
                {countdown > 0 && (
                  <span className="otp-countdown">
                    ({Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')})
                  </span>
                )}
              </label>
              <input
                type="text"
                id="otp"
                name="otp"
                value={resetData.otp}
                onChange={handleResetChange}
                placeholder="Enter confirmation code from email"
                required
              />
              <button 
                type="button" 
                className="resend-otp-btn"
                onClick={resendOTP}
                disabled={countdown > 0 || loading}
              >
                {loading ? 'Sending...' : countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}
              </button>
            </div>

            <button 
              type="submit" 
              className="auth-submit-btn"
              disabled={loading || !resetData.otp}
            >
              {loading ? 'Confirming...' : 'Confirm Email'}
            </button>

            <button 
              type="button" 
              className="auth-back-btn"
              onClick={() => {
                resetForm();
                setIsLogin(false);
              }}
            >
              ← Back to Registration
            </button>
          </form>
        ) : isForgotPassword ? (
          <form onSubmit={resetStep === 1 ? (e) => { e.preventDefault(); handleForgotPassword(); } : handleResetPassword}>
            {resetStep === 1 ? (
              <>
                <div className="form-group">
                  <label htmlFor="reset-email">
                    <MailIcon size={16} />
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="reset-email"
                    name="email"
                    value={resetData.email}
                    onChange={handleResetChange}
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <button 
                  type="submit" 
                  className="auth-submit-btn"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send OTP'}
                </button>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label htmlFor="otp">
                    <KeyIcon size={16} />
                    Verification Code (OTP)
                    {countdown > 0 && (
                      <span className="otp-countdown">
                        ({Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')})
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    id="otp"
                    name="otp"
                    value={resetData.otp}
                    onChange={handleResetChange}
                    placeholder="Enter 6-digit OTP"
                    maxLength="6"
                    pattern="\d{6}"
                    required
                  />
                  <button 
                    type="button" 
                    className="resend-otp-btn"
                    onClick={resendOTP}
                    disabled={countdown > 0 || loading}
                  >
                    {loading ? 'Sending...' : countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
                  </button>
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword">
                    <LockIcon size={16} />
                    New Password
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      id="newPassword"
                      name="newPassword"
                      value={resetData.newPassword}
                      onChange={handleResetChange}
                      placeholder="Enter new password"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeClosedIcon size={16} /> : <EyeIcon size={16} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmNewPassword">
                    <LockIcon size={16} />
                    Confirm New Password
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmNewPassword ? "text" : "password"}
                      id="confirmNewPassword"
                      name="confirmNewPassword"
                      value={resetData.confirmNewPassword}
                      onChange={handleResetChange}
                      placeholder="Confirm new password"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                    >
                      {showConfirmNewPassword ? <EyeClosedIcon size={16} /> : <EyeIcon size={16} />}
                    </button>
                  </div>
                </div>

                <div className="password-requirements">
                  <p>Password must contain:</p>
                  <ul>
                    {passwordValidation.errors.map((req, index) => (
                      <li key={index} className={req.condition ? 'valid' : 'invalid'}>
                        {req.condition ? '✓' : '✗'} {req.message}
                      </li>
                    ))}
                  </ul>
                </div>

                <button 
                  type="submit" 
                  className="auth-submit-btn"
                  disabled={loading || !passwordValidation.isValid}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </>
            )}

            <button 
              type="button" 
              className="auth-back-btn"
              onClick={handleBackToLogin}
            >
              ← Back to Login
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit}>
            {!isLogin && (
              <div className="form-group">
                <label htmlFor="name">
                  <PersonIcon size={16} />
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  required={!isLogin}
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">
                <MailIcon size={16} />
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">
                <LockIcon size={16} />
                Password
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeClosedIcon size={16} /> : <EyeIcon size={16} />}
                </button>
              </div>
            </div>

            {!isLogin && (
              <div className="form-group">
                <label htmlFor="confirmPassword">
                  <LockIcon size={16} />
                  Confirm Password
                </label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your password"
                    required={!isLogin}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeClosedIcon size={16} /> : <EyeIcon size={16} />}
                  </button>
                </div>
              </div>
            )}

            {!isLogin && (
              <div className="password-requirements">
                <p>Password must contain:</p>
                <ul>
                  {passwordValidation.errors.map((req, index) => (
                    <li key={index} className={req.condition ? 'valid' : 'invalid'}>
                      {req.condition ? '✓' : '✗'} {req.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {isLogin && (
              <div className="auth-options">
                <button 
                  type="button" 
                  className="forgot-password-btn"
                  onClick={() => setIsForgotPassword(true)}
                >
                  Forgot Password?
                </button>
              </div>
            )}

            <button 
              type="submit" 
              className="auth-submit-btn"
              disabled={loading || (!isLogin && !passwordValidation.isValid)}
            >
              {loading ? 'Processing...' : isLogin ? 'Login' : 'Register'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export const UserProfile = ({ user, onLogout }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const getUserInitials = () => {
    if (!user) return '?';
    
    if (user.name) {
      const nameParts = user.name.split(' ');
      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    
    if (user.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    
    return 'U';
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('email');
    localStorage.removeItem('userName');
    
    setIsDropdownOpen(false);
    onLogout();
  };

  return (
    <div className="user-profile-container">
      <button 
        className="user-profile-btn"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        aria-expanded={isDropdownOpen}
        aria-label="User menu"
      >
        <div className="user-avatar">
          {getUserInitials()}
        </div>
      </button>
      
      {isDropdownOpen && (
        <>
          <div 
            className="dropdown-backdrop"
            onClick={() => setIsDropdownOpen(false)}
          />
          <div className="user-profile-dropdown">
            <div className="user-info">
              <div className="user-details">
                <span className="user-name">{user?.name || 'User'}</span>
              </div>
            </div>
            
            <div className="dropdown-divider"></div>
            
            <button 
              className="logout-btn"
              onClick={handleLogout}
            >
              <SignOutIcon size={16} />
              Logout
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AuthModal;
